import {
  fetchLatestTableState,
  fetchTableStateByWidgetId,
} from '@/store/thunks/FetchTable';
import {
  NewTableColumn,
  tableCellFactory,
  TableColumn,
  TableRow,
  tableRowFactory,
  tableWidgetFactory,
} from '@/models/Table';
import { openWidget, OpenWidgetPayload } from '@/store/reducers/UIState';
import { TableToolPaper } from '@/actions/ProcessUIToolCalls';
import { TableWidgetData, WidgetType } from '@/models/PanelWidgets';
import { ThreadId } from '@/models/Thread';

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TableWidgetStoreState {
  stateForThreads: Record<ThreadId, TableWidgetData>;
}

export interface AddOrRemovePapersToTablePayload {
  currentThreadId: ThreadId;
  papers: TableToolPaper[];
}

interface AddRowsToTablePayload {
  currentThreadId: ThreadId;
  rows: TableRow[];
}

export interface RemoveRowsFromTablePayload {
  currentThreadId: ThreadId;
  rowIds: string[];
}

export interface UpdateTableRowPayload {
  currentThreadId: ThreadId;
  row: TableRow;
}

export interface UpdateTableColumnPayload {
  currentThreadId: ThreadId;
  column: TableColumn;
}

export interface AddNewColumnsPayload {
  currentThreadId: string;
  columns: NewTableColumn[];
}

export interface AddColumnsToTablePayload {
  currentThreadId: ThreadId;
  columns: TableColumn[];
}

export interface RemoveColumnsFromTablePayload {
  currentThreadId: ThreadId;
  columnIds: string[];
}

export interface RowValue {
  [rowCorpusId: string]: string;
}

export interface BulkUpdateRowsPayload {
  currentThreadId: string;
  updates: Partial<TableRow>;
}

export interface CacheTablePayload {
  threadId: ThreadId;
  tableData: TableWidgetData;
}

export interface EditTableCellPayload {
  currentThreadId: ThreadId;
  cellId: string;
  editedValue: string | null;
  editedByUUID: string | null;
}

const initialState: TableWidgetStoreState = {
  stateForThreads: {},
};

/*************
 * reducers *
 *************/
export const cacheDataForThreadReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<CacheTablePayload>,
) => {
  const { threadId, tableData } = action.payload;
  state.stateForThreads[threadId] = tableData;
};

export const openTableWidgetReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<OpenWidgetPayload>,
) => {
  const { threadId, widgetData } = action.payload;
  if (widgetData?.type !== WidgetType.TABLE) {
    return;
  }

  // If the widget data is null, we don't want to overwrite the existing table data
  const existingTableData = state.stateForThreads[threadId];
  if (!widgetData && !!existingTableData) {
    return;
  }

  // but if there is no existing table, we want to create a new table data object
  const dataToCache = widgetData ?? tableWidgetFactory();

  cacheDataForThreadReducer(state, {
    type: 'tableWidgetStore/cacheDataForThread',
    payload: {
      threadId,
      tableData: dataToCache,
    },
  });
};

export const addRowsToTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<AddRowsToTablePayload>,
) => {
  const { currentThreadId, rows } = action.payload;
  const emptyTable = tableWidgetFactory();
  const tableData = state.stateForThreads[currentThreadId] ?? emptyTable;
  const newTableData = getTableDataWithAddedPapers(rows, tableData);
  state.stateForThreads[currentThreadId] = newTableData;
};

export const addPapersToTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<AddOrRemovePapersToTablePayload>,
) => {
  const { currentThreadId, papers } = action.payload;
  const emptyTable = tableWidgetFactory();
  const tableData = state.stateForThreads[currentThreadId] ?? emptyTable;
  const newTableData = getTableDataWithAddedPapers(papers, tableData);
  state.stateForThreads[currentThreadId] = newTableData;
};

export const addColumnsToTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<AddColumnsToTablePayload>,
) => {
  const { currentThreadId, columns } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }
  tableData.columns = [...tableData.columns, ...columns];
};

export const updateTableColumnReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<UpdateTableColumnPayload>,
) => {
  const { currentThreadId, column } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }

  const existingColumnIdx = tableData.columns.findIndex(
    (col) => col.id === column.id,
  );

  if (existingColumnIdx !== -1) {
    tableData.columns.splice(existingColumnIdx, 1, column);
  }
};

export const updateTableRowReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<UpdateTableRowPayload>,
) => {
  const { currentThreadId, row: updatedRow } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }

  const existingRowIdx = tableData.rows.findIndex(
    (row) => row.id === updatedRow.id,
  );

  if (existingRowIdx !== -1) {
    tableData.rows.splice(existingRowIdx, 1, updatedRow);
  }
};

export const bulkUpdateRowsReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<BulkUpdateRowsPayload>,
) => {
  const { currentThreadId, updates } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }

  tableData.rows = tableData.rows.map((row) => {
    const updatedRow: TableRow = { ...row, ...updates };
    return updatedRow;
  });
};

export const editTableCellReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<EditTableCellPayload>,
) => {
  const { currentThreadId, cellId, editedValue, editedByUUID } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }

  const tableCells = { ...tableData.cells };

  tableCells[cellId].editedValue = editedValue;
  tableCells[cellId].editedByUUID = editedByUUID;

  tableData.cells = tableCells;
};

export const removeColumnsFromTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<RemoveColumnsFromTablePayload>,
) => {
  const { currentThreadId, columnIds } = action.payload;
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }
  const columnIdsToRemove = new Set(columnIds);
  tableData.columns = tableData.columns.filter(
    (col) => !columnIdsToRemove.has(col.id),
  );
};

export const removePapersFromTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<AddOrRemovePapersToTablePayload>,
) => {
  const { currentThreadId, papers } = action.payload;
  const corpusIds = papers.map((paper) => paper.corpusId);
  const corpusIdsToRemove = new Set(corpusIds);
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }
  tableData.rows = tableData.rows.filter(
    (row) => row.paperCorpusId && !corpusIdsToRemove.has(row.paperCorpusId),
  );
};

export const removeRowsFromTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<RemoveRowsFromTablePayload>,
) => {
  const { currentThreadId, rowIds } = action.payload;
  const rowIdsToRemove = new Set(rowIds);
  const tableData = state.stateForThreads[currentThreadId];
  if (!tableData) {
    return;
  }
  tableData.rows = tableData.rows.filter((row) => !rowIdsToRemove.has(row.id));
};

export const setLoadingTableCellsReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<{
    threadId: string;
    columns?: TableColumn[];
    rows?: TableRow[];
  }>,
) => {
  const { threadId, columns = null, rows = null } = action.payload;
  const tableData = state.stateForThreads[threadId];

  if (!tableData) {
    return;
  }

  const tableCells = { ...tableData.cells };
  const tableColumns = columns ?? tableData.columns;
  const tableRows = rows ?? tableData?.rows;
  for (const row of tableRows) {
    for (const column of tableColumns) {
      const cellKey = createCellKey(row.id, column.id);
      tableCells[cellKey] = tableCellFactory({ isLoading: true });
    }
  }
  tableData.cells = tableCells;
};

export const resetTableReducer = (
  state: TableWidgetStoreState,
  action: PayloadAction<ThreadId>,
) => {
  const threadId = action.payload;
  state.stateForThreads[threadId] = tableWidgetFactory({
    id: threadId,
  });
};

/*************
 * selectors *
 *************/
export const getTableStateForThreadSelector = (
  state: TableWidgetStoreState,
  threadId: ThreadId | undefined,
): TableWidgetData | null => {
  if (!threadId) {
    return null;
  }
  return state.stateForThreads[threadId];
};

export const getNumSelectedRowsInTableSelector = createSelector(
  [
    (state: TableWidgetStoreState, threadId?: string) =>
      getTableStateForThreadSelector(state, threadId),
  ],
  (tableData: TableWidgetData | null) => {
    if (!tableData) {
      return 0;
    }

    return tableData.rows.filter((row) => row.isSelected).length;
  },
);

/*************
 * slice *
 *************/
export const tableWidgetStoreSlice = createSlice({
  name: 'tableWidgetStore',
  initialState,
  reducers: {
    addColumnsToTable: addColumnsToTableReducer,
    bulkUpdateRows: bulkUpdateRowsReducer,
    cacheDataForThread: cacheDataForThreadReducer,
    editTableCell: editTableCellReducer,
    removeColumnsFromTable: removeColumnsFromTableReducer,
    addPapersToTable: addPapersToTableReducer,
    addRowsToTable: addRowsToTableReducer,
    removePapersFromTable: removePapersFromTableReducer,
    removeRowsFromTable: removeRowsFromTableReducer,
    resetTable: resetTableReducer,
    setLoadingTableCells: setLoadingTableCellsReducer,
    updateTableColumn: updateTableColumnReducer,
    updateTableRow: updateTableRowReducer,
  },
  extraReducers: (builder) => {
    builder
      .addCase(openWidget, openTableWidgetReducer)
      .addCase(fetchLatestTableState.fulfilled, cacheDataForThreadReducer)
      .addCase(fetchTableStateByWidgetId.fulfilled, cacheDataForThreadReducer);
  },
  selectors: {
    getNumSelectedRowsInTable: getNumSelectedRowsInTableSelector,
    getTableStateForThread: getTableStateForThreadSelector,
  },
});

export const { getNumSelectedRowsInTable, getTableStateForThread } =
  tableWidgetStoreSlice.selectors;
export const {
  addColumnsToTable,
  bulkUpdateRows,
  cacheDataForThread,
  editTableCell,
  removeColumnsFromTable,
  addPapersToTable,
  addRowsToTable,
  removePapersFromTable,
  removeRowsFromTable,
  resetTable,
  setLoadingTableCells,
  updateTableColumn,
  updateTableRow,
} = tableWidgetStoreSlice.actions;
export const tableWidgetStoreReducer = tableWidgetStoreSlice.reducer;

/*************
 * helpers *
 *************/
export function createCellKey(rowId: string, columnId: string): string {
  return `${rowId}_${columnId}`;
}

export function getColumnIdFromCellKey(cellKey: string): string {
  return cellKey.split('_')[1];
}

export function getRowIdFromCellKey(cellKey: string): string {
  return cellKey.split('_')[0];
}

const getTableDataWithAddedPapers = (
  papers: TableRow[] | TableToolPaper[],
  tableData: TableWidgetData,
): TableWidgetData => {
  const rows = tableData.rows ?? [];
  const updatedRows = [
    ...rows,
    ...papers.map((paper) =>
      'corpusId' in paper
        ? tableRowFactory({
            paperCorpusId: paper.corpusId,
            displayValue: paper.title,
          })
        : paper,
    ),
  ];
  return {
    ...tableData,
    rows: updatedRows,
  };
};
